import { useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import {
  createAgreementTemplate,
  getAgreementTemplateErrorMessage,
  updateAgreementTemplate,
  getAllActiveTempPlaceHolders,
} from "../../../api/agreementTemplateApi";import { FORMS, MODULES } from "../../../const";
import Button from "../../input/Button";
import Dropdown from "../../input/Dropdown";
import Input from "../../input/Input";
import { agreementTemplateValidation } from "../../validations/AgreementTemplate";
import { getBulkDropdownData } from "../../../api/keyvalue";
import Editor, {
  BtnBold,
  BtnBulletList,
  BtnClearFormatting,
  BtnItalic,
  BtnLink,
  BtnNumberedList,
  BtnRedo,
  BtnStrikeThrough,
  BtnStyles,
  BtnUnderline,
  BtnUndo,
  createButton,
  Toolbar,
} from "react-simple-wysiwyg";
import { FaAlignCenter, FaAlignLeft, FaAlignRight } from "react-icons/fa";

// Defined at module scope so React never remounts the editor on re-renders
const BtnAlignCenter = createButton(
  "Align center",
  <FaAlignCenter size={12} />,
  "justifyCenter"
);
const BtnAlignRight = createButton(
  "Align right",
  <FaAlignRight size={12} />,
  "justifyRight"
);
const BtnAlignLeft = createButton(
  "Align left",
  <FaAlignLeft size={12} />,
  "justifyLeft"
);

// MS Office-style font size list
const FONT_SIZE_OPTIONS = [8, 9, 10, 11, 12, 14, 16, 18, 20, 22, 24, 26, 28, 36, 48, 72];

/**
 * Reads the computed px font-size at the current cursor/selection position.
 */
function getCurrentFontSize() {
  const sel = window.getSelection();
  if (!sel || sel.rangeCount === 0) return "";
  const node = sel.anchorNode;
  const el = node ? (node.nodeType === 3 ? node.parentElement : node) : null;
  if (!el) return "";
  const px = parseFloat(window.getComputedStyle(el).fontSize);
  if (!px) return "";
  // Convert px -> pt  (1pt = 1.333px)
  const pt = Math.round(px * 0.75);
  return FONT_SIZE_OPTIONS.includes(pt) ? String(pt) : "";
}


function applyFontSize(pt) {
  const px = Math.round(pt * (4 / 3)); // pt → px
  const sel = window.getSelection();
  if (!sel || sel.rangeCount === 0 || sel.isCollapsed) return;

  document.execCommand("styleWithCSS", false, false);
  document.execCommand("fontSize", false, "7");

  const placeholders = document.querySelectorAll('font[size="7"]');
  placeholders.forEach((node) => {
    node.removeAttribute("size");
    node.style.fontSize = `${px}px`;
  });
}

function FontSizeDropdown({ size, setSize, ignoreNextRef }) {

  const handleEditorSelectionChange = () => {
    if (ignoreNextRef && ignoreNextRef.current) {
      ignoreNextRef.current = false;
      return;
    }
    setSize(getCurrentFontSize());
  };

  useEffect(() => {
    document.addEventListener("selectionchange", handleEditorSelectionChange);
    return () => document.removeEventListener("selectionchange", handleEditorSelectionChange);
  }, []);

  const handleChange = (e) => {
    const pt = Number(e.target.value);
    if (!pt) return;
    setSize(String(pt));
    applyFontSize(pt);
  };

  return (
    <select
      value={size}
      onChange={handleChange}
      title="Font Size"
      style={{
        height: "26px",
        fontSize: "12px",
        border: "1px solid #ccc",
        borderRadius: "3px",
        padding: "0 2px",
        cursor: "pointer",
        minWidth: "10px",
        maxWidth: "10%",
        verticalAlign: "middle",
        marginLeft: "2px",
        marginRight: "2px",
      }}
    >
      <option value="">pt</option>
      {FONT_SIZE_OPTIONS.map((pt) => (
        <option key={pt} value={pt}>
          {pt}
        </option>
      ))}
    </select>
  );
}

function CustomEditor({ value, onChange }) {
  const [fontSize, setFontSize] = useState("");
  // Ref flag: tells FontSizeDropdown to skip the next selectionchange
  const ignoreNextSelection = useRef(false);

  return (
    <Editor
      value={value}
      onChange={onChange}
      containerProps={{
        style: {
          minHeight: "300px",
          height: "auto",
          resize: "vertical",
        },
      }}
    >
      <Toolbar>
        <BtnRedo />
        <BtnUndo />
        <BtnBold />
        <BtnItalic />
        <BtnUnderline />
        <BtnStrikeThrough />
        <FontSizeDropdown size={fontSize} setSize={setFontSize} ignoreNextRef={ignoreNextSelection} />
        <BtnAlignLeft />
        <BtnAlignCenter />
        <BtnAlignRight />
        <BtnBulletList />
        <BtnNumberedList />
        <BtnClearFormatting />
        <BtnLink />
        {/* onMouseDown fires BEFORE selectionchange, so we can set the flag in time */}
        <span
          onMouseDown={() => {
            ignoreNextSelection.current = true;
            setFontSize("");
          }}
        >
          <BtnStyles />
        </span>
      </Toolbar>
    </Editor>
  );
}

const DEFAULT_FORM = {
  agtCode: "",
  agtTitle: "",
  agtSubject: "",
  agtTemplate: "",
  agtType: "",
  agtLanguage: "EN",
  agtActive: 1,
  agtStatus: 2,
};

const AgreementTemplateForm = ({
  editMode = false,
  currentValues = {},
  onSuccess = () => {},
  onCancel = () => {},
}) => {
  const user = useSelector((state) => state.user);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState({});

  const [formData, setFormData] = useState(DEFAULT_FORM);

  const [activeOptions, setActiveOptions] = useState([]);
  const [statusOptions, setStatusOptions] = useState([]);
  const [placeholders, setPlaceholders] = useState([]);

  useEffect(() => {
    (async () => {
      const { active, status } = await getBulkDropdownData([
        { key: "active" },
        { key: "status" },
      ]);
      setActiveOptions(active);
      setStatusOptions(status);

      try {
        const phData = await getAllActiveTempPlaceHolders();
        console.log("phData-->",phData);
        setPlaceholders(Array.isArray(phData) ? phData : phData?.data || []);
      } catch (err) {
        console.error("Failed to fetch placeholders", err);
      }
    })();
  }, []);

  useEffect(() => {
    if (editMode && currentValues && Object.keys(currentValues).length > 0) {
      setFormData({
        agtCode: currentValues.agtCode || "",
        agtTitle: currentValues.agtTitle || "",
        agtSubject: currentValues.agtSubject || "",
        agtTemplate: currentValues.agtTemplate || "",
        agtType:
          currentValues.agtType === undefined || currentValues.agtType === null
            ? ""
            : String(currentValues.agtType),
        agtLanguage: currentValues.agtLanguage || "EN",
        agtActive:
          currentValues.agtActive === undefined ||
          currentValues.agtActive === null
            ? 1
            : currentValues.agtActive,
        agtStatus:
          currentValues.agtStatus === undefined ||
          currentValues.agtStatus === null
            ? 2
            : currentValues.agtStatus,
      });
    } else {
      setFormData({ ...DEFAULT_FORM });
    }
    setErrors({});
  }, [editMode, currentValues]);

  const payload = useMemo(() => {
    return {
      ...formData,
      agtType:
        formData.agtType === "" || formData.agtType === null
          ? null
          : Number(formData.agtType),
      author: user?.username || "system",
    };
  }, [formData, user?.username]);

  const setField = (key, value) => {
    setFormData((prev) => ({ ...prev, [key]: value }));
    setErrors((prev) => {
      const { [key]: _, ...rest } = prev;
      return rest;
    });
  };

  const handleSubmit = async () => {
    const validationErrors = agreementTemplateValidation({
      agtTitle: formData.agtTitle,
      agtTemplate: formData.agtTemplate,
    });

    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      toast.error("Please fill all required fields.", {
        autoClose: 5000,
        containerId: "Main",
      });
      return;
    }

    setIsSubmitting(true);
    try {
      if (editMode) {
        await updateAgreementTemplate({
          ...payload,
          agtId: currentValues.agtId,
        });
        toast.success("Agreement template updated successfully.", {
          autoClose: 5000,
          containerId: "Main",
        });
      } else {
        await createAgreementTemplate(payload);
        toast.success("Agreement template created successfully.", {
          autoClose: 5000,
          containerId: "Main",
        });
      }
      onSuccess();
    } catch (error) {
      toast.error(getAgreementTemplateErrorMessage(error), {
        autoClose: 5000,
        containerId: "Main",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="ui form" style={{ padding: "1rem" }}>
      <h4 className="ui dividing header">
        {editMode ? "Update Agreement Template" : "Create Agreement Template"}
      </h4>

      <div className="two fields">
        <Input
          required={true}
          label="Template Title"
          name="agtTitle"
          value={formData.agtTitle}
          error={errors.agtTitle}
          onChange={(e) => setField("agtTitle", e.target.value)}
        />
        <Input
          label="Subject"
          name="agtSubject"
          value={formData.agtSubject}
          error={errors.agtSubject}
          onChange={(e) => setField("agtSubject", e.target.value)}
        />
      </div>

      <div className="two fields">
        <Input
          label="Type"
          name="agtType"
          type="number"
          value={formData.agtType}
          error={errors.agtType}
          onChange={(e) => setField("agtType", e.target.value)}
        />
        <Input
          label="Language"
          name="agtLanguage"
          value={formData.agtLanguage}
          error={errors.agtLanguage}
          onChange={(e) => setField("agtLanguage", e.target.value)}
        />
      </div>

      <div className="two fields">
        <Dropdown
          required={true}
          label="Active"
          options={activeOptions}
          value={formData.agtActive}
          error={errors.agtActive}
          onChange={(e, data) => setField("agtActive", data.value)}
        />
        <Dropdown
          required={true}
          label="Status"
          options={statusOptions}
          value={formData.agtStatus}
          error={errors.agtStatus}
          onChange={(e, data) => setField("agtStatus", data.value)}
        />
      </div>

      <div className={`field ${errors.agtTemplate ? "error" : ""}`}>
        <label>
          <label
            style={{ color: "#9f3a38", fontSize: "20px", marginRight: "2px" }}
          >
            *
          </label>
          Agreement Template Editor
        </label>

        <div style={{ display: "flex", gap: "1rem", alignItems: "flex-start" }}>


          <div
            style={{
              width: "250px",
              maxHeight: "350px",
              overflowY: "auto",
              border: "1px solid #ddd",
              borderRadius: "4px",
              padding: "10px",
              backgroundColor: "#f9fafb",
            }}
          >
            <h5
              className="ui header"
              style={{ marginTop: 0, marginBottom: "10px", fontSize: "14px" }}
            >
              Available Agreement Variables
            </h5>
            <p style={{ fontSize: "12px", color: "gray", marginBottom: "10px", lineHeight: "1.2" }}>
              Drag and drop items below into the editor.
            </p>
            {placeholders && placeholders.length > 0 ? (
              placeholders.map((item, idx) => (
                <div
                  key={idx}
                  className="ui horizontal label"
                  draggable={true}
                  onDragStart={(e) => {
                    e.dataTransfer.setData(
                      "text/plain",
                      `{${item.tphPlaceholderKey}}`
                    );
                  }}
                  style={{
                    cursor: "grab",
                    marginBottom: "8px",
                    display: "inline-block",
                    whiteSpace: "normal",
                  }}
                  title={`Entity: ${item.tphEntityName || "Unknown"}`}
                >
                  {item.tphPlaceholderKey}
                </div>
              ))
            ) : (
              <div style={{ fontSize: "12px", color: "gray" }}>
                No variables available
              </div>
            )}
          </div>
          <div style={{ flex: "1" }}>
            <CustomEditor
                value={formData.agtTemplate}
                onChange={(e) => setField("agtTemplate", e.target.value)}
            />
            {errors.agtTemplate && (
                <small style={{ color: "#9f3a38" }}>{errors.agtTemplate}</small>
            )}
          </div>
        </div>
      </div>

      <div
        style={{
          width: "100%",
          display: "flex",
          alignItems: "center",
          justifyContent: "flex-end",
          marginTop: "1rem",
        }}
      >
        <Button label="Cancel" className="ui button" onClick={onCancel} />
        <Button
          template="create"
          type="button"
          isLoading={isSubmitting}
          create={!editMode}
          editMode={editMode}
          onClick={handleSubmit}
          form={FORMS.AGREEMENT.AGR.form}
          module={MODULES.Master}
          authenticated={true}
        />
      </div>
    </div>
  );
};

export default AgreementTemplateForm;
